import {
  DatePicker,
  FormsCheckBox,
  FormsInputDropdown,
  FormsMultilineInput,
  InlineFormInput,
  InputDropdownItem,
} from '@platform/components';
import { AdminMicrostories, AdminSearch } from '@platform/client';
import { MicrostorySource, SimplifiedUserRole } from '@platform/client/schemas';
import { Control, Controller, FormState, UseFormRegister } from 'react-hook-form';
import { formatEnumLabel } from './formatEnumLabel';
import { FormsNameSearchDropdown } from './FormsNameSearchDropdown';

export type MicrostoryFormData = {
  contactName: string;
  organizationName: string;
  microstory: string;
  userType?: SimplifiedUserRole;
  source: MicrostorySource;
  storyDate: Date;
  urlOrFileRef?: string | null;
  consentToShare: boolean;
  corporateClientId?: string | null;
  impactPartnerId?: string | null;
  userId?: number | null;
  organizationId?: number | null;
};

type MicrostoryFormProps = {
  control: Control<MicrostoryFormData>;
  formState: FormState<MicrostoryFormData>;
  register: UseFormRegister<MicrostoryFormData>;
};

const USER_TYPE_ITEMS: InputDropdownItem[] = [
  SimplifiedUserRole.PROFESSIONAL,
  SimplifiedUserRole.REPRESENTATIVE,
].map((value) => ({
  text: formatEnumLabel(value.toLowerCase()),
  textValue: value,
}));

const SOURCE_ITEMS: InputDropdownItem[] = Object.values(MicrostorySource).map((value) => ({
  text: formatEnumLabel(value),
  textValue: value,
}));

const useMicrostoryUserSearch = (query: string) => {
  const { data, isFetching } = AdminSearch.useSearchUsersByName(
    { query },
    { query: { enabled: query.length >= 2 } },
  );
  return { options: data?.options ?? [], isFetching };
};

const useMicrostoryOrganizationSearch = (query: string) => {
  const { data, isFetching } = AdminSearch.useSearchOrganizationsByName(
    { query },
    { query: { enabled: query.length >= 2 } },
  );
  return { options: data?.options ?? [], isFetching };
};

export const MicrostoryForm = (props: MicrostoryFormProps) => {
  const { control, formState, register } = props;
  const { data: filterOptions } = AdminMicrostories.useGetMicrostoryFilterOptions();

  const corporateClientItems: InputDropdownItem[] = (filterOptions?.corporateClients ?? []).map(
    (client) => ({ text: client.name, textValue: String(client.id) }),
  );
  const impactPartnerItems: InputDropdownItem[] = (filterOptions?.impactPartners ?? []).map(
    (partner) => ({ text: partner.name, textValue: String(partner.id) }),
  );

  return (
    <div className='flex flex-col gap-4 md:min-w-[800px] pb-[118px]'>
      <FormsNameSearchDropdown
        control={control}
        nameField='contactName'
        idField='userId'
        label='Contact Name'
        rules={{ required: 'Contact name is required' }}
        errorText={formState.errors.contactName?.message}
        useSearch={useMicrostoryUserSearch}
      />
      <FormsNameSearchDropdown
        control={control}
        nameField='organizationName'
        idField='organizationId'
        label='Organization'
        errorText={formState.errors.organizationName?.message}
        useSearch={useMicrostoryOrganizationSearch}
      />
      <FormsInputDropdown
        type='default'
        control={control}
        name='userType'
        label='User Type'
        items={USER_TYPE_ITEMS}
        error={formState.errors.userType}
      />
      <FormsInputDropdown
        type='default'
        control={control}
        name='source'
        label='Source'
        items={SOURCE_ITEMS}
        rules={{ required: 'Source is required' }}
        error={formState.errors.source}
      />
      <FormsInputDropdown
        type='default'
        control={control}
        name='corporateClientId'
        label='Corporate Client'
        items={corporateClientItems}
      />
      <FormsInputDropdown
        type='default'
        control={control}
        name='impactPartnerId'
        label='Impact Partner'
        items={impactPartnerItems}
      />
      <Controller
        name='storyDate'
        control={control}
        rules={{ required: 'Date is required' }}
        render={({ field }) => (
          <DatePicker
            label='Date'
            initialDate={field.value ?? new Date()}
            onChange={(newDate) => field.onChange(newDate)}
          />
        )}
      />
      <InlineFormInput
        prefixContent='https://'
        register={() => register('urlOrFileRef')}
        label='URL / File Ref'
        errors={formState.errors.urlOrFileRef}
        customWidth='w-full'
      />
      <div className='h-48'>
        <FormsMultilineInput
          control={control}
          name='microstory'
          label='Microstory'
          placeholder='Paste or type the story here...'
          rules={{ required: 'Microstory is required' }}
          formsError={formState.errors.microstory}
          stretchHeight
        />
      </div>
      <FormsCheckBox
        size='sm'
        control={control}
        name='consentToShare'
        description='User has given consent to share this story'
      />
    </div>
  );
};
